import * as React from 'react';
import { useCallback } from 'react';
import Modal from '../../layout/modal/Modal';
import { Str } from '../../localization/translationKeys';
import LocalTextArea from '../../localization/LocalTextArea';
import LocalInput from '../../localization/LocalInput';
import ModalHeader from '../../layout/modal/ModalHeader';
import LocalDiv from '../../localization/LocalDiv';
import LocalBtn from '../../localization/LocalBtn';
import Spacer from '../../layout/Spacer';
import { FLEX_ROW, FLEX_COLUMN } from '../../layout/flexBox';
import { css } from '@emotion/core';
import { btnStyle } from '../../forms/styles/button';
import { COLORS } from '../../globalStyle/colors';
import { useMsgText } from '../../localization/hooks';
import { INPUT } from '../../forms/styles/input';
import { TEXT_AREA } from '../../forms/styles/textarea';
import { BREAKPOINTS_PX, onMobileMedium, HIDE_ON_MOBILE } from '../../globalStyle/mediaQueries';


const styles = {
  root:             [
    css`
      @media only screen and (min-width: ${BREAKPOINTS_PX.MOBILE + 1}px) {
        min-width: 780px;
      }
    `,
  ],
  form:             [
    FLEX_COLUMN,
    css`
      margin: 16px 60px;

      ${onMobileMedium} {
        margin-left: 40px;
        margin-right: 40px;
      }
    `,
  ],
  input:            [
    INPUT(COLORS.WHITE),
  ],
  textArea:         [
    TEXT_AREA,
    css`
      font-size: 13px;
    `,
  ],
  previewContainer: css`
    margin-top: 30px;
    margin-bottom: 45px;

    ${onMobileMedium} {
      margin-top: 15px;
      margin-bottom: 15px;
    }
  `,
  emailPreview:     css`
    margin-top: auto;
    background-color: ${COLORS.WHITE};
    border: 0 none;
    min-height: 300px;
    width: 100%;

    ${onMobileMedium} {
      min-height: calc(100vh - 270px);
    }
  `,
  btnRow:           [
    FLEX_ROW,
    css`
      justify-content: flex-end;
    `,
  ],
  secondaryBtn:     [
    btnStyle.secondary(COLORS.BLUE),
    css`
      min-width: 135px;
    `,
  ],
  primaryBtn:       [
    btnStyle.primary(COLORS.BLUE),
    css`
      min-width: 135px;
    `,
  ],
};


type Props = {
  dismissModal: () => void;
  setEQScoresRecipientEmail: (email: string) => void;
  setEQScoresRecipientComment: (comment: string) => void;
  onConfirmShareEQScores: () => void;
  templatePreviewRoute: string;
  className?: string;
};

/**
 * SendEQScoresModal: a component that
 */
const SendEQScoresModal: React.FC<Props> = (
  {
    dismissModal,
    setEQScoresRecipientEmail,
    setEQScoresRecipientComment,
    onConfirmShareEQScores,
    templatePreviewRoute,
    className,
  },
) => {
  const handleChangeRecipientEmail = useCallback(
    event => setEQScoresRecipientEmail(event.target.value),
    [setEQScoresRecipientEmail],
  );

  const handleChangeComment = useCallback(
    event => setEQScoresRecipientComment(event.target.value),
    [setEQScoresRecipientComment],
  );

  const handleConfirmShareEmailScores = useCallback(
    event => {
      event.preventDefault();
      onConfirmShareEQScores();
    },
    [onConfirmShareEQScores],
  );

  const modalCaptionMsg        = useMsgText(Str.ShareScores);
  const emailPreviewCaptionMsg = useMsgText(Str.EmailPreview);

  return (
    <Modal onDismiss={dismissModal} css={styles.root}>
      <ModalHeader dismissable={false} title={modalCaptionMsg} />
      <form css={styles.form}>
        <LocalInput
          placeholderTranslation=''
          type='text'
          onChange={handleChangeRecipientEmail}
          css={styles.input}
        />
        <Spacer sizePx={20} />
        <LocalTextArea
          placeholderTranslation={Str.AddOptionalText}
          onChange={handleChangeComment}
          css={styles.textArea}
        />
        <LocalDiv css={[FLEX_COLUMN, styles.previewContainer]}>
          <LocalDiv css={css`margin-bottom: 8px;`}>
            {emailPreviewCaptionMsg}
          </LocalDiv>
          <iframe
            allowFullScreen={true}
            css={styles.emailPreview}
            title='eq-scores-template-body'
            src={templatePreviewRoute}
          />
        </LocalDiv>
        <Spacer sizePx={20} css={HIDE_ON_MOBILE} />
        <LocalDiv css={styles.btnRow}>
          <LocalBtn
            onClick={dismissModal}
            str={Str.Cancel}
            css={styles.secondaryBtn}
          />
          <Spacer sizePx={10} />
          <LocalBtn
            onClick={handleConfirmShareEmailScores}
            str={Str.Share}
            css={styles.primaryBtn}
          />
        </LocalDiv>
      </form>
    </Modal>
  );
};

export default SendEQScoresModal;
